// src/pages/PreferencesPage.tsx -- Slice L / P5.
//
// Dedicated screen for app preferences (theme skin + home logo). Matches
// the pattern of /me/details and /me/privacy: tap a card on /me, navigate
// here, save explicitly, hard-reload back to home so theme changes apply
// globally (same shim as the onboarding hotfix until auth-in-Context
// lands).
//
// Previously this was an inline AppPreferencesPanel that click-to-saved
// each pick. Replaced by this page; the old panel was removed from MePage.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser, type LogoKey, type ThemeKey } from "../auth";
import { THEMES, THEME_LABELS } from "../lib/themes";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, TopBar } from "../components/ui";

const LOGO_LABELS: Record<LogoKey, string> = {
  "art-deco-train": "Art Deco Train",
  "nymr-logo":      "NYMR Logo",
};

const LOGO_KEYS: ReadonlyArray<LogoKey> = ["art-deco-train", "nymr-logo"];

export function PreferencesPage() {
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const initialTheme: ThemeKey = me?.preferences?.theme ?? "warm-gold";
  const initialLogo: LogoKey = me?.preferences?.logo ?? "art-deco-train";

  const [theme, setTheme] = useState<ThemeKey>(initialTheme);
  const [logo, setLogo] = useState<LogoKey>(initialLogo);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (me?.preferences?.theme && me.preferences.theme !== theme) {
      setTheme(me.preferences.theme);
    }
    if (me?.preferences?.logo && me.preferences.logo !== logo) {
      setLogo(me.preferences.logo);
    }
    // Sync only when the SERVER preferences change. Including the local
    // `theme`/`logo` picks would re-fire this and clobber the user's unsaved
    // selection the instant they choose a new skin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.preferences?.theme, me?.preferences?.logo]);

  const dirty = theme !== initialTheme || logo !== initialLogo;

  async function save() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch("/api/me/preferences", {
        method: "PUT",
        body: JSON.stringify({ theme, logo }),
      });
      // Hard reload to /me so the theme + logo apply globally without
      // having to plumb refresh() through every consumer of useCurrentUser.
      // Same shim that the onboarding flow uses (commit 3d0659b).
      window.location.replace("/me");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save preferences");
      setSaving(false);
    }
  }

  return (
    <div>
      <TopBar title="App preferences" sub="Theme and home icon" onBack={() => navigate("/me")} />
      <div style={{ padding: "14px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        <Card>
          <Eyebrow>Skin</Eyebrow>
          <p
            style={{
              fontFamily: F.body,
              fontSize: 13,
              color: C.inkSoft,
              fontWeight: 600,
              margin: "8px 0 12px",
              lineHeight: 1.4,
            }}
          >
            Pick the palette the hub renders in. Applies everywhere once you save.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(Object.keys(THEMES) as ThemeKey[]).map((key) => {
              const active = theme === key;
              const tokens = THEMES[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTheme(key)}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 999,
                    border: `1.5px solid ${active ? tokens.accent : C.hairline}`,
                    background: active ? tokens.accentBg : C.card,
                    color: active ? tokens.accent : C.inkSoft,
                    fontFamily: F.body,
                    fontWeight: 800,
                    fontSize: 13,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    letterSpacing: 0.3,
                  }}
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 12,
                      background: tokens.accent,
                      border: `1px solid ${tokens.ink}33`,
                    }}
                    aria-hidden="true"
                  />
                  {THEME_LABELS[key]}
                </button>
              );
            })}
          </div>
        </Card>

        <Card>
          <Eyebrow>Home icon</Eyebrow>
          <p
            style={{
              fontFamily: F.body,
              fontSize: 13,
              color: C.inkSoft,
              fontWeight: 600,
              margin: "8px 0 12px",
              lineHeight: 1.4,
            }}
          >
            Choose the decoration shown in the top-right of the home page.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {LOGO_KEYS.map((key) => {
              const active = logo === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setLogo(key)}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 999,
                    border: `1.5px solid ${active ? C.brassDk : C.hairline}`,
                    background: active ? C.brassBg : C.card,
                    color: active ? C.brassDk : C.inkSoft,
                    fontFamily: F.body,
                    fontWeight: 800,
                    fontSize: 13,
                    cursor: "pointer",
                    letterSpacing: 0.3,
                  }}
                >
                  {LOGO_LABELS[key]}
                </button>
              );
            })}
          </div>
        </Card>

        {error && (
          <div
            role="alert"
            style={{
              padding: 12,
              borderRadius: 12,
              background: C.signalBg,
              border: `1px solid rgba(184,58,43,0.22)`,
              color: C.signalDk,
              fontFamily: F.body,
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <Button
            variant="primary"
            fullWidth
            disabled={saving || !dirty}
            onClick={save}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="ghost"
            disabled={saving}
            onClick={() => navigate("/me")}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
